import React, { useState, useEffect } from 'react';
import { Edit, Trash2, X, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import Table from "../../base-component/Table/Table";
import { coachingAccreditationData } from "../../base-component/Data";
import Pagination from "../../base-component/Pagination/Pagination";
import Dialog from "../../base-component/Dialog/Dialog";
import { getAccreditationsApi } from '../../Services/ApiServices';
import { updateAccreditationsApi } from '../../Services/ApiServices';
import { addAccreditationsApi } from '../../Services/ApiServices';
import { deleteAccreditationsApi } from '../../Services/ApiServices';
import toast from 'react-hot-toast';

const CoachingAccreditation = () => {
  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAccreditation, setSelectedAccreditation] = useState(null);
  const [editedAccreditationName, setEditedAccreditationName] = useState("");
  const [newAccreditationName, setNewAccreditationName] = useState("");
  const [accreditations, setAccreditations] = useState([...coachingAccreditationData]);

  const handleEdit = (accreditation) => {
    setSelectedAccreditation(accreditation);
    setEditedAccreditationName(accreditation.accreditation);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (accreditation) => {
    setSelectedAccreditation(accreditation);
    setIsDeleteDialogOpen(true);
  };

  const handleEditConfirm = (values) => {
    if (selectedAccreditation && values.accreditation.trim()) {
      setAccreditations(accreditations.map(acc => 
        acc.id === selectedAccreditation.id 
          ? { ...acc, accreditation: values.accreditation.trim() }
          : acc
      ));
    }
    let params = {
      accreditation_id: selectedAccreditation.id,
      accreditation: values.accreditation.trim()
    };
    updateAccreditationsApi(params)
      .then(res => {
        toast.success("Accreditation updated successfully");
        fetchAccreditations();
      })
      .catch(err => toast.error(err.response.data.message));
    setIsEditDialogOpen(false);
    setSelectedAccreditation(null);
    setEditedAccreditationName("");
  };

  const handleDeleteConfirm = () => {
    if (!selectedAccreditation) return;
  
    let params = {
      accreditation_id: selectedAccreditation.id,
      accreditation: selectedAccreditation.accreditation, // <-- required
    };
  
    deleteAccreditationsApi(params)
      .then(res => {
        toast.success("Accreditation deleted successfully");
        setAccreditations(prev => prev.filter(acc => acc.id !== selectedAccreditation.id));
        setIsDeleteDialogOpen(false);
        setSelectedAccreditation(null);
      })
      .catch(err => {
        console.error(err);
        toast.error(err?.response?.data?.message || "Failed to delete accreditation");
      });
  };
  
  const handleAddAccreditation = () => {
    setNewAccreditationName("");
    setIsAddDialogOpen(true);
  };

  const handleAddConfirm = (values) => {
 
    let params = {
      accreditation: values.accreditation.trim()
    };
    addAccreditationsApi(params)
      .then(res => {
        toast.success("Accreditation added successfully");
        fetchAccreditations();
      })
      .catch(err => toast.error(err.response.data.message));
    setIsAddDialogOpen(false);
    setNewAccreditationName("");
  };

  const validationSchema = Yup.object().shape({
    accreditation: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters")
      .trim(),
  });


  useEffect(() => {
    fetchAccreditations();
  }, [pageNo]);

  const fetchAccreditations = () => {
    let params = {
      page: pageNo
    };
    getAccreditationsApi(params)
      .then(res => {
        setAccreditations(res.data.data);
        setPageCount(res.data.totalPages);
      })
      .catch(err => {
        console.log(err);
        toast.error(err.response.data.message);
      });
  };
  
  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const renderPaginationButtons = () => {
    const maxPagesToShow = 3;
    const buttons = [];
    const startPage = Math.max(1, pageNo - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(pageCount, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={
            i === pageNo
              ? "bg-[#2C5BA7] text-white w-10 h-10 rounded-lg font-medium text-xl"
              : "text-[#4B4B4B] w-10 h-10 rounded-lg font-medium text-xl"
          }
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <p className="text-xl p-0 m-0 font-bold text-gray-900 tracking-tight">Coaching Accreditation</p>

        <button
          onClick={handleAddAccreditation}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#2C5BA7] text-white rounded-lg hover:bg-[#1e4a8a] transition-colors font-medium"
        >
          <Plus size={18} />
          Add Accreditation
        </button>
      </div>

      {/* Table Wrapper */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <Table>
          <Table.Thead variant="light">
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>ACCREDITATION NAME</Table.Th>
              <Table.Th>CREATED AT</Table.Th>
              <Table.Th className="text-center">ACTIONS</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {accreditations.map((accreditation, index) => (
              <Table.Tr key={accreditation.id}>
                {/* ID */}
                <Table.Td className="font-semibold text-slate-700">
                {(pageNo - 1) * 10 + index + 1}

                </Table.Td>

                {/* Accreditation Name */}
                <Table.Td className="text-slate-600 font-medium">
                  {accreditation.accreditation}
                </Table.Td>

                {/* Created At */}
                <Table.Td className="text-slate-600">{moment(accreditation.created_at).format("DD-MM-YYYY h:mm A") || "—"}</Table.Td>

                {/* Actions */}
                <Table.Td>
                  <div className="flex items-center justify-center gap-4">
                    {/* Edit */}
                    <button
                      onClick={() => handleEdit(accreditation)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#10B981',
                        backgroundColor: '#D1FAE5',
                        color: '#10B981'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#059669';
                        e.target.style.backgroundColor = '#A7F3D0';
                        e.target.style.color = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#10B981';
                        e.target.style.backgroundColor = '#D1FAE5';
                        e.target.style.color = '#10B981';
                      }}
                    >
                      <Edit size={16} />
                    </button>

                    {/* Delete */}
                    <button 
                      onClick={() => handleDelete(accreditation)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#F87171',
                        backgroundColor: '#FEF2F2',
                        color: '#F87171'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#ef4444';
                        e.target.style.backgroundColor = '#FEE2E2';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#F87171';
                        e.target.style.backgroundColor = '#FEF2F2';
                        e.target.style.color = '#F87171';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>

                  </div>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {/* Pagination */}
      {accreditations.length > 0 && (
        <div className="flex mt-5 items-center col-span-12 justify-between">
          <Pagination>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo - 1)}
                disabled={pageNo == 1}
              >
                <ArrowLeft />
              </button>
            </Pagination.Link>
            <div className="flex items-center">
              {renderPaginationButtons()}
            </div>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo + 1)}
                disabled={pageNo === pageCount}
              >
                <ArrowRight />
              </button>
            </Pagination.Link>
          </Pagination>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedAccreditation(null);
          setEditedAccreditationName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedAccreditation(null);
                  setEditedAccreditationName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Edit Accreditation
              </div>

              <Formik
                initialValues={{ accreditation: editedAccreditationName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => handleEditConfirm(values)}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Accreditation Name
                      </label>
                      <Field
                        type="text"
                        name="accreditation"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.accreditation && touched.accreditation
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter accreditation name"
                      />
                      <ErrorMessage
                        name="accreditation"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Update
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setNewAccreditationName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setNewAccreditationName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Add Accreditation
              </div>

              <Formik
                initialValues={{ accreditation: newAccreditationName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  handleAddConfirm(values);
                  setNewAccreditationName("");
                }}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Accreditation Name
                      </label>
                      <Field
                        type="text"
                        name="accreditation"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.accreditation && touched.accreditation
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter accreditation name"
                      />
                      <ErrorMessage
                        name="accreditation"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Add
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedAccreditation(null);
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="px-4 py-4 text-center relative" style={{ backgroundColor: '#FEF2F2' }}>
              <button
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setSelectedAccreditation(null);
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <Trash2 size={56} className="text-[#F87171]" />
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-8 text-center w-full">
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-4">
                Delete Accreditation?
              </div>

              <Dialog.Description className="md:text-base text-sm text-gray-600 mb-6 leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-gray-900">{selectedAccreditation?.accreditation}</span>? This action cannot be undone.
              </Dialog.Description>

              <button
                onClick={handleDeleteConfirm}
                className="w-full px-6 h-12 shadow cursor-pointer bg-[#F87171] text-white font-medium rounded-xl hover:bg-[#ef4444] transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>
    </>
  );
};

export default CoachingAccreditation;
